import { useState } from 'react';
import { Button, Col, Form, Row, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { formatProfitMarginRange } from '../utils/rangeUtils';
import { checkRangesGap, checkRangesOverlap } from '../utils/rangeUtils';
import { toast } from 'react-toastify';

const ProfitMarginRangesEditor = ({ ranges, onRangesChange, mode = 'create' }) => {
	const [newRange, setNewRange] = useState({
		minMargin: '',
		minOperator: '<',
		maxMargin: '',
		maxOperator: '≤',
		performanceRate: '',
	});

	const handleAddRange = () => {
		const min = newRange.minMargin ? parseFloat(newRange.minMargin) : null;
		const max = newRange.maxMargin ? parseFloat(newRange.maxMargin) : null;
		const rateInput = parseFloat(newRange.performanceRate);

		// 验证最小利润率（可以为空，表示无下限）
		if (newRange.minMargin && newRange.minMargin.trim() !== '') {
			if (isNaN(min)) {
				toast.error('最小利润率必须是有效数字');
				return;
			}
			if (min < 0) {
				toast.error('最小利润率不能小于0');
				return;
			}
		}

		// 验证最大利润率
		if (max !== null) {
			if (isNaN(max)) {
				toast.error('最大利润率必须是有效数字');
				return;
			}
			// 如果最小利润率不为null，检查最大利润率是否大于最小利润率
			if (min !== null && max <= min) {
				toast.error('最大利润率必须大于最小利润率');
				return;
			}
		}

		// 验证绩效比例（输入是0-100，需要转换为0-1）
		if (isNaN(rateInput) || rateInput < 0 || rateInput > 100) {
			toast.error('绩效比例必须在0-100之间（0表示0%，100表示100%）');
			return;
		}
		// 将百分比转换为小数（0-1）
		const rate = rateInput / 100;

		// 检查是否重叠
		const newRangeObj = {
			minMargin: min,
			minOperator: newRange.minOperator || '<',
			maxMargin: max,
			maxOperator: newRange.maxOperator || '≤',
			performanceRate: rate,
		};

		// 检查新分段与所有现有分段是否重叠
		for (const existingRange of ranges) {
			const overlap = checkRangesOverlap(newRangeObj, existingRange);
			if (overlap) {
				toast.error('利润率分段不能重叠');
				return;
			}
		}

		// 添加新分段后，检查所有分段是否有空隙
		// 在添加分段的过程中（无论是创建模式还是编辑模式），都使用宽松模式（strictMode = false）
		// 允许用户逐步构建分段，不强制要求最后一个分段到 +∞
		// 只有在最终提交表单时，才使用严格模式检查
		const allRanges = [...ranges, newRangeObj];
		const strictMode = false; // 添加分段时始终使用宽松模式
		const gapCheck = checkRangesGap(allRanges, strictMode);
		if (gapCheck.hasGap) {
			// 忽略关于"最后一个分段必须到 +∞"的错误，因为在添加过程中允许最后一个分段不到 +∞
			if (gapCheck.message.includes('最后一个分段的最大利润率必须为空')) {
				// 在添加分段的过程中，允许最后一个分段不到 +∞，不阻止用户添加
			} else {
				toast.error(gapCheck.message);
				return;
			}
		}

		onRangesChange(allRanges);
		setNewRange({ minMargin: '', minOperator: '<', maxMargin: '', maxOperator: '≤', performanceRate: '' });
		toast.success('分段添加成功');
	};

	const handleRemoveRange = (index) => {
		const newRanges = ranges.filter((_, i) => i !== index);
		onRangesChange(newRanges);
	};

	const sortedRanges = [...ranges].sort((a, b) => {
		if (a.minMargin === null && b.minMargin === null) return 0;
		if (a.minMargin === null) return -1;
		if (b.minMargin === null) return 1;
		return a.minMargin - b.minMargin;
	});

	return (
		<div>
			<Form.Label className='fw-semibold text-900 d-flex align-items-center mb-2'>
				利润率分段
				<OverlayTrigger
					trigger='click'
					placement='top'
					rootClose
					overlay={
						<Tooltip style={{ position: 'fixed' }} id='profit-margin-ranges-tooltip'>
							<div style={{ textAlign: 'left' }}>
								最小值留空表示无下限（-∞），最大值留空表示无上限（+∞）。例如：10%&lt;A≤20% 表示利润率大于10%且小于等于20%时，按设定的绩效比例计算。
							</div>
						</Tooltip>
					}
				>
					<button type='button' className='ms-2 bg-transparent border-0 p-0 text-primary' style={{ cursor: 'pointer' }} aria-label='查看说明'>
						<FontAwesomeIcon icon='question-circle' />
					</button>
				</OverlayTrigger>
			</Form.Label>

			{ranges.length === 0 ? (
				<div className='text-center py-3 text-muted border rounded bg-100 mb-3'>
					<FontAwesomeIcon icon='inbox' className='fs-6 d-block mx-auto' style={{ width: '1.2rem' }} />
					<small>暂无分段，请添加分段规则</small>
				</div>
			) : (
				<div className='border rounded mb-3 bg-100 overflow-hidden'>
					<div className='table-responsive'>
						<Table className='table-sm table-hover mb-0'>
							<thead className='bg-200'>
								<tr>
									<th className='text-900 fw-semibold' style={{ fontSize: '0.75rem', width: '10%' }}>
										序号
									</th>
									<th className='text-900 fw-semibold' style={{ fontSize: '0.75rem', width: '30%' }}>
										利润率范围
									</th>
									<th className='text-900 fw-semibold' style={{ fontSize: '0.75rem', width: '25%' }}>
										绩效比例
									</th>
									<th className='text-900 fw-semibold text-center' style={{ fontSize: '0.75rem', width: '35%' }}>
										操作
									</th>
								</tr>
							</thead>
							<tbody>
								{sortedRanges.map((range, index) => {
									const originalIndex = ranges.findIndex((r) => r.minMargin === range.minMargin && r.maxMargin === range.maxMargin && r.performanceRate === range.performanceRate);
									return (
										<tr key={`range-${originalIndex}-${range.minMargin}-${range.maxMargin}-${range.performanceRate}`}>
											<td className='align-middle'>
												<span className='badge bg-primary'>{index + 1}</span>
											</td>
											<td className='align-middle'>
												<span className='fw-semibold text-900'>{formatProfitMarginRange(range)}</span>
											</td>
											<td className='align-middle'>
												<span className='text-primary fw-bold'>{(range.performanceRate * 100).toFixed(1)}%</span>
											</td>
											<td className='align-middle text-center'>
												<Button variant='outline-danger' size='sm' onClick={() => handleRemoveRange(originalIndex)} title='删除此分段' className='border-0'>
													<FontAwesomeIcon icon='trash-alt' className='me-1' />
												</Button>
											</td>
										</tr>
									);
								})}
							</tbody>
						</Table>
					</div>
				</div>
			)}

			<div className='border rounded p-2 bg-100'>
				<h6 className='small mb-2 fw-semibold text-900 d-flex align-items-center'>
					<FontAwesomeIcon icon='plus-circle' className='me-2 text-primary' />
					{mode === 'create' ? '分段设定' : '添加新分段'}
				</h6>
				<Row className='g-2 align-items-end'>
					<Col xs={12} sm={2}>
						<Form.Group className='mb-0'>
							<Form.Label className='small fw-semibold mb-0' style={{ fontSize: '0.75rem' }}>
								最小值 (%)
							</Form.Label>
							<Form.Control
								type='number'
								step='0.01'
								placeholder={mode === 'create' ? '-∞' : '留空表示无下限'}
								size='sm'
								value={newRange.minMargin}
								onChange={(e) => setNewRange({ ...newRange, minMargin: e.target.value })}
							/>
						</Form.Group>
					</Col>
					<Col xs={12} sm={2}>
						<Form.Group className='mb-0'>
							<Form.Label className='small fw-semibold mb-0' style={{ fontSize: '0.75rem' }}>
								最小值运算符
							</Form.Label>
							<Form.Select
								className='small fw-semibold mb-0'
								style={{ fontSize: '0.75rem' }}
								value={newRange.minOperator || '<'}
								onChange={(e) => setNewRange({ ...newRange, minOperator: e.target.value })}
							>
								<option value='<'>{`<`}</option>
								<option value='≤'>{`≤`}</option>
							</Form.Select>
						</Form.Group>
					</Col>
					<Col xs={12} sm={1}>
						<span className='small fw-semibold d-flex align-items-center justify-content-center text-center ' style={{ fontSize: '1.5rem', height: '100%' }}>
							~
						</span>
					</Col>
					<Col xs={12} sm={2}>
						<Form.Group className='mb-0'>
							<Form.Label className='small fw-semibold mb-0' style={{ fontSize: '0.75rem' }}>
								最大值运算符
							</Form.Label>
							<Form.Select
								className='small fw-semibold mb-0'
								style={{ fontSize: '0.75rem' }}
								value={newRange.maxOperator || '≤'}
								onChange={(e) => setNewRange({ ...newRange, maxOperator: e.target.value })}
							>
								<option value='<'>{`<`}</option>
								<option value='≤'>{`≤`}</option>
							</Form.Select>
						</Form.Group>
					</Col>
					<Col xs={12} sm={2}>
						<Form.Group className='mb-0'>
							<Form.Label className='small fw-semibold mb-0' style={{ fontSize: '0.75rem' }}>
								最大值 (%)
							</Form.Label>
							<Form.Control
								type='number'
								step='0.01'
								placeholder={mode === 'create' ? '+∞' : '留空表示无上限'}
								size='sm'
								value={newRange.maxMargin}
								onChange={(e) => setNewRange({ ...newRange, maxMargin: e.target.value })}
							/>
						</Form.Group>
					</Col>
					<Col xs={12} sm={2}>
						<Form.Group className='mb-0'>
							<Form.Label className='small fw-semibold mb-0' style={{ fontSize: '0.75rem' }}>
								绩效比例 <span className='text-danger'>*</span>
							</Form.Label>
							<Form.Control
								type='number'
								step='0.01'
								min='0'
								max='100'
								placeholder='0-100'
								size='sm'
								value={newRange.performanceRate}
								onChange={(e) => setNewRange({ ...newRange, performanceRate: e.target.value })}
							/>
						</Form.Group>
					</Col>
					<Col xs={12} sm={1}>
						<Form.Group className='mb-0'>
							<Form.Label className='small fw-semibold mb-0' style={{ fontSize: '0.75rem', visibility: 'hidden' }}>
								操作
							</Form.Label>
							<Button
								variant='primary'
								size='sm'
								onClick={handleAddRange}
								disabled={!newRange.performanceRate}
								className='w-100'
								title={!newRange.performanceRate ? '请先填写绩效比例' : '添加分段'}
							>
								<FontAwesomeIcon icon='plus' />
							</Button>
						</Form.Group>
					</Col>
				</Row>
			</div>
		</div>
	);
};

ProfitMarginRangesEditor.propTypes = {
	ranges: PropTypes.array.isRequired,
	onRangesChange: PropTypes.func.isRequired,
	mode: PropTypes.oneOf(['create', 'edit']),
};

export default ProfitMarginRangesEditor;
